import configs from "../../lib/configs";
import {
    getBranding,
    getBrandingAndDashboard,
    getContentByCategory,
    getContentItemById,
    getDashboard,
    getStream,
    getWatchList,
    getWatchListUncategorized
} from "../../api/queries";
import posthog from "../../lib/posthog";
import { refreshUserData, STORAGE_KEYS } from "../../lib/utils";
import { Storage } from "@lightningjs/sdk";
import widgets from "../../lib/widgets";
import { Vod } from "./Vod";
import { MovieSingle } from "./MovieSingle";
import { SeriesSingle } from "./SeriesSingle";
import { Playback } from "./Playback";
import { Favorite } from "./Favorite";
import { Trailer } from "./Trailer";
import { MediaCategoryName } from "../../components/data-display/MediaCategoryName/MediaCategoryName";
import { ExternalApp } from "../../lib/models";

const singleMediaWidgets = configs.layout.menu.fullWidth
    ? widgets
    : widgets.filter((w) => w !== <Lowercase<string>>"Menu");

export default [
    {
        path: "vod",
        component: Vod,
        options: {
            clearHistory: true
        },
        on: (page: any) => {
            return new Promise<void>((resolve) => {
                // Lightning Router holds the Boot splash until on() resolves. We deliberately
                // keep it up and keep retrying rather than falling back to an empty dashboard —
                // users should see the loading state, never a "Content not available yet"
                // message caused by a slow/failed backend on launch.
                const RETRY_DELAY_MS = 3000;
                let appsCategory: any;

                (async () => {
                    try {
                        if (configs.modules.apps && window.Android && window.Android.getAppList) {
                            const apps = window.Android.getAppList();
                            appsCategory = {
                                id: "apps",
                                name: "Apps",
                                items: (typeof apps === "string" ? JSON.parse(apps) : apps).map((app: ExternalApp) => ({
                                    __typename: "ExternalApp",
                                    id: app.id,
                                    title: app.name,
                                    coverUrl: app.logo,
                                    backdropUrl: "",
                                    isFree: false
                                }))
                            };
                        }
                    } catch (e) {
                        console.warn("[vod loader] appsCategory build failed:", e);
                    }

                    // eslint-disable-next-line no-constant-condition
                    while (true) {
                        let dashboard: any[] = [];
                        let branding: any = undefined;

                        try {
                            if (configs.hasBranding) {
                                try {
                                    const result = await getBrandingAndDashboard();
                                    branding = result?.branding;
                                    dashboard = result?.dashboard || [];
                                } catch (batchError) {
                                    console.warn("[vod loader] batch query failed, falling back:", batchError);
                                    const [brandingResult, dashboardResult] = await Promise.allSettled([
                                        getBranding().catch(() => null),
                                        getDashboard()
                                    ]);
                                    if (brandingResult.status === "fulfilled" && brandingResult.value) {
                                        branding = brandingResult.value;
                                    }
                                    if (dashboardResult.status === "fulfilled") {
                                        dashboard = dashboardResult.value || [];
                                    } else {
                                        console.warn("[vod loader] dashboard fetch failed:", dashboardResult.reason);
                                    }
                                }
                            } else {
                                dashboard = (await getDashboard()) || [];
                            }
                        } catch (e) {
                            console.warn("[vod loader] content fetch failed:", e);
                        }

                        if (dashboard.length > 0) {
                            if (appsCategory) dashboard.unshift(appsCategory);
                            page.branding = branding;
                            page.data = dashboard;
                            resolve();
                            return;
                        }

                        console.warn(`[vod loader] no content yet, retrying in ${RETRY_DELAY_MS}ms`);
                        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "movies",
        component: MediaCategoryName,
        options: {
            clearHistory: true
        },
        on: (page: any) => {
            return new Promise<void>((resolve, reject) => {
                (async () => {
                    try {
                        // Load fewer items initially for faster first render, pagination will load more
                        page.data = await getContentByCategory("movie", 0, 20);
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "series",
        component: MediaCategoryName,
        options: {
            clearHistory: true
        },
        on: (page: any) => {
            return new Promise<void>((resolve, reject) => {
                (async () => {
                    try {
                        page.showFilterCategories = false;
                        // Load fewer items initially for faster first render, pagination will load more
                        page.data = await getContentByCategory("series", 0, 20);
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "documentaries",
        component: MediaCategoryName,
        options: {
            clearHistory: true
        },
        on: (page: any) => {
            return new Promise<void>((resolve, reject) => {
                (async () => {
                    try {
                        // Load fewer items initially for faster first render, pagination will load more
                        page.data = await getContentByCategory("documentary", 0, 20);
                        page.showFilterCategories = false;
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "category/:categoryId",
        component: MediaCategoryName,
        on: (page: any, { categoryId }: any) => {
            return new Promise<void>((resolve, reject) => {
                (async () => {
                    try {
                        if (categoryId === "apps" && window.Android?.getAppList) {
                            const apps = window.Android.getAppList()
                            page.data = {
                                id: "apps",
                                name: "Apps",
                                items: (typeof apps === "string" ? JSON.parse(apps) : apps).map((app: ExternalApp) => ({
                                    __typename: "ExternalApp",
                                    id: app.id,
                                    title: app.name,
                                    coverUrl: app.logo,
                                    backdropUrl: "",
                                    isFree: false
                                }))
                            };
                        } else {
                            page.data = await getContentByCategory(categoryId, 0, 20);
                        }
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "movie/:movieId",
        component: MovieSingle,
        on: (page: any, { movieId }: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    page.data = await getContentItemById(movieId, "Movie");

                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        widgets: singleMediaWidgets
    },
    {
        path: "series/:movieId",
        component: SeriesSingle,
        on: (page: any, { movieId }: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    page.data = await getContentItemById(movieId, "TvSeries");

                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        widgets: widgets
    },
    {
        path: "playback/:movieId",
        component: Playback,
        on: (page: any, { movieId }: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    let data;
                    data = await getStream(movieId);
		    const user = Storage.get(STORAGE_KEYS.user);
                    posthog.capture("Playback", {
                        id: data.movie?.id || "",
                        title: data.movie?.title || "",
                        isVodafoneUser: user?.whiteListed || false,
                        whiteListed: user?.whiteListed || false
                    });

                    // data[0].url = "http://sample.vodobox.com/planete_interdite/planete_interdite_alternate.m3u8";
                    // data[0].url = "https://media.axprod.net/TestVectors/v7-Clear/Manifest_1080p.mpd";
                    // data[0].url = "https://livesim.dashif.org/dash/vod/testpic_2s/cea608.mpd";

                    // data[0].drm = undefined;
                    //     "https://test.playready.microsoft.com/service/rightsmanager.asmx?PlayRight=1&ContentKey=EAtsIJQPd5pFiRUrV9Layw==";

                    page.data = data;
                    resolve();
                } catch (e) {
                    if (navigator.onLine) {
                        await refreshUserData(true);

                        const user = Storage.get(STORAGE_KEYS.user);
                        if (!user?.subscribed) {
                            page.redirect = true;

                            resolve();
                        } else {
                            reject(e);
                        }
                    } else {
                        reject(e);
                    }
                }
            });
        }
    },
    {
        path: "favorite",
        component: Favorite,
        on: (page: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    const isGridView =
                        ((configs as any).app?.layout?.bookmarks?.view ?? configs.layout?.bookmarks?.view) === "grid";
                    page.data = isGridView ? await getWatchListUncategorized() : await getWatchList();

                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        widgets: widgets
    },
    {
        path: "trailer",
        component: Trailer,
        on: (page: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    page.data = window.selectedMovie?.trailer?.url;

                    resolve();
                } catch (e) {
                    console.log(e);
                    reject(e);
                }
            });
        }
    }
];
